import * as React from 'react';
import * as _ from 'lodash';
import { useDispatch } from 'react-redux';
import {
  ControlState,
  ControlStyleClass,
  Notification,
  Logging,
  API,
  NotificationData,
  ResponseError,
  UserInfo,
  UserContext,
  ContentLoading,
  Jurisdiction,
  Footer,
  getUuidV4,
  renderValidationMessage,
} from '@vcd-ecm-ui/Common';
import { VtxButton, VtxIcon, VtxTree } from '@vertexinc/vtx-ui-react-component-library';
import JurisdictionsProps from './JurisdictionsProps.interface';
import useJurisdictions from './useJurisdictions';
import '../common-ecwizard.scss';
import './jurisdictions.scss';

interface TreeData {
  title: string;
  key: string;
  checkable: boolean;
  selectable: boolean;
  disabled: boolean;
  children: TreeData[];
}

const showSelectionCount = true;

const Jurisdictions = ({
  gatewayEndPoint,
}: JurisdictionsProps): JSX.Element => {
  const [reloadKey, setReloadKey] = React.useState(getUuidV4());
  const [isLoading, setIsLoading] = React.useState(true);

  // Load the Jurisdictions
  const [jurisdictions, jurisdictionsNotification] = useJurisdictions(gatewayEndPoint, reloadKey, true);

  const [treeData, setTreeData] = React.useState(null);

  const [selectedJurisdictionKeys, setSelectedJurisdictionKeys] = React.useState(null);

  const dispatch = useDispatch();

  const [notification, setNotification] = React.useState(null);

  // Save the Jurisdictions
  const saveJurisdictions = (jurisdictionIds: number[]) => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      Logging.Debug('Set Jurisdictions:', jurisdictionIds);

      // Set the Jurisdictions
      await api.ecwizard.setJurisdictionsV41(jurisdictionIds);

      const notificationData: NotificationData = {
        type: 'Information',
        title: 'Jurisdictions Saved',
        description: 'The Jurisdictions were saved.',
        autoClose: true,
        autoCloseTimeOutMilliseconds: 1000,
        position: 'BottomRight',
      };

      setNotification(notificationData);

      // Reload the Jurisdictions
      setReloadKey(getUuidV4());
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Jurisdictions Save Error',
        description: 'Unable to Save the Jurisdictions.',
        error: error as ResponseError,
      };

      setNotification(notificationData);
    }
  };

  // Jurisiction Sorter Function - Sort alphabetically by name
  const compareJurisdictions = (a: Jurisdiction, b: Jurisdiction): number => {
    // Use toUpperCase() to ignore character casing
    const nameA = a.name.toUpperCase();
    const nameB = b.name.toUpperCase();

    let comparison = 0;

    if (nameA > nameB) {
      comparison = 1;
    } else if (nameA < nameB) {
      comparison = -1;
    }

    return comparison;
  };

  // Get the Number of Selected Jurisdiction under this Jurisdiction
  const getNumberSelectedChildren = (jurisdiction: Jurisdiction, seletedJurisdictions: string[]): number => {
    if (showSelectionCount) {
      // Get the Children for the Jurisdiction
      const children: Jurisdiction[] = jurisdictions.filter((childJurisdiction: Jurisdiction) => (childJurisdiction.parentId === jurisdiction.id));

      // Get the Ids of the Children
      const childrenId: number[] = children?.map((x) => x.id);

      // Get the Selected Children
      const selected: number[] = childrenId?.map((x) => (seletedJurisdictions?.includes(x.toString()) ? 1 : 0));

      // Get the Number of Selected Children
      const numberSelected = selected.reduce((a, b) => a + b, 0);

      // Get the Selected Children of Children
      const selectedChildren: number[] = children?.map((childJurisdiction: Jurisdiction) => {
        Logging.Debug('Child:', childJurisdiction.name);
        return getNumberSelectedChildren(childJurisdiction, seletedJurisdictions);
      });

      // Get the Number of Selected Children of Children
      const numberSelectedChildren = selectedChildren.reduce((a, b) => a + b, 0);

      // Return the Total
      return numberSelected + numberSelectedChildren;
    }

    return 0;
  };

  // Get the Tree Data for the Jurisdiction
  const getJurisdictionData = (jurisdiction: Jurisdiction, seletedJurisdictions: string[]): TreeData => {
    // Get the Children for the Jurisdiction
    const children: Jurisdiction[] = jurisdictions.filter((childJurisdiction: Jurisdiction) => (childJurisdiction.parentId === jurisdiction.id));

    if (children.length) {
      Logging.Debug('Parent:', jurisdiction.name, children.length);
    }

    // Get the Number of Selected Jurisdiction under this Jurisdiction
    const numberSelected = getNumberSelectedChildren(jurisdiction, seletedJurisdictions);

    const jurisdictionTreeData: TreeData = {
      title: numberSelected ? `${jurisdiction.name} (${numberSelected})` : jurisdiction.name,
      key: jurisdiction.id.toString(),
      selectable: false,
      checkable: jurisdiction.parentId !== null,
      disabled: false,
      // Loop through the Children of the Jurisdiction
      children: children?.sort(compareJurisdictions).map((childJurisdiction: Jurisdiction) => {
        Logging.Debug('Child:', childJurisdiction.name);
        return getJurisdictionData(childJurisdiction, seletedJurisdictions);
      }),
    };

    return jurisdictionTreeData;
  };

  // Set the Tree Data
  const onSetTreeData = (seletedJurisdictions: string[]): void => {
    if (jurisdictions) {
      Logging.Debug('Jurisdictions:', jurisdictions);

      // Loop through the Root Jurisdictions
      const data: TreeData[] = jurisdictions.filter((jurisdiction: Jurisdiction) => (jurisdiction.parentId === null)).sort(compareJurisdictions).map((rootJurisdiction: Jurisdiction) => { // eslint-disable-line max-len
        Logging.Debug('Root Jurisdiction:', rootJurisdiction.name);

        return getJurisdictionData(rootJurisdiction, seletedJurisdictions);
      });

      setTreeData(data);
    }
  };

  // Set the Default Tree Data
  const onSetDefaultTreeData = (): void => {
    if (jurisdictions) {
      // Get teh selected Jurisdictions.  Ignore the Root Jurisdictions
      const seletedJurisdictions: string[] = jurisdictions.filter((jurisdiction: Jurisdiction) => (jurisdiction.isApplicableToClient === true) && (jurisdiction.parentId !== null)) // eslint-disable-line max-len
        .map((jurisdiction: Jurisdiction) => jurisdiction.id.toString());

      Logging.Debug('Jurisdictions:', jurisdictions, seletedJurisdictions);

      onSetTreeData(seletedJurisdictions);

      setSelectedJurisdictionKeys(seletedJurisdictions);
    }
  };

  // Save Button was Pressed
  const onSave = (): void => {
    // Convert the Selected Jurisdiction Keys from Strings to numbers
    const jurisdictionIds: number[] = selectedJurisdictionKeys.map((jurisdictionKey: string) => Number(jurisdictionKey));

    dispatch(saveJurisdictions(jurisdictionIds));
  };

  // Cancel Button was Pressed
  const onCancel = (): void => {
    onSetDefaultTreeData();
  };

  // Notification Error was Closed
  const onCloseNotification = (): void => {
    setNotification(null);
  };

  // Select All Jurisdictions
  const onSelectAll = (): void => {
    // Get all the Jurisdictions.  Ignore the Root Jurisdictions
    const seletedJurisdictions: string[] = jurisdictions?.filter((jurisdiction: Jurisdiction) => (jurisdiction.parentId !== null))
      .map((jurisdiction: Jurisdiction) => jurisdiction.id.toString());

    setSelectedJurisdictionKeys(seletedJurisdictions);

    if (showSelectionCount) {
      // Update the Tree Data
      onSetTreeData(seletedJurisdictions);
    }
  };

  // Clear All Jurisdictions
  const onClearAll = (): void => {
    setSelectedJurisdictionKeys([]);

    if (showSelectionCount) {
      // Update the Tree Data
      onSetTreeData([]);
    }
  };

  // Jurisdictions has Checked / Unchecked
  const onCheck = (keys: any, info: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
    Logging.Debug('onCheck', keys, info);

    // Save the Selected Keys
    setSelectedJurisdictionKeys(keys.checked);

    if (showSelectionCount) {
      // Update the Tree Data
      onSetTreeData(keys.checked);
    }
  };

  const validateValues = (): boolean => {
    // One Jurisdiction must at least be selected
    if (selectedJurisdictionKeys?.length === 0) {
      return false;
    }

    return true;
  };

  // Jurisdictions were loaded
  React.useEffect(() => {
    if (jurisdictions) {
      // Set the Default Tree Data
      onSetDefaultTreeData();

      setIsLoading(false);
    }
  }, [jurisdictions]);

  // Unable to Load the Jurisdictions
  React.useEffect(() => {
    if (jurisdictionsNotification) {
      setIsLoading(false);

      setNotification(jurisdictionsNotification);
    }
  }, [jurisdictionsNotification]);

  React.useEffect(() => {
    // This Code will run once after the initial Render
    Logging.Debug('Jurisidctions: Mount');

    return (): void => {
      // This Code will run once the component unmounts.
      setIsLoading(true);
      setNotification(null);
    };
  }, []);

  const canSave = validateValues();
  const validationMessage = !canSave ? 'At least one Jurisdiction must be selected.' : null;
  const totalJurisdictions = jurisdictions?.filter((jurisdiction: Jurisdiction) => (jurisdiction.parentId !== null)).length;
  const selectionInformation = `${selectedJurisdictionKeys?.length} of ${totalJurisdictions} Jurisdictions selected`;

  return (
    <>
      {
        isLoading
          ? (
            <ContentLoading loadingMessage="Loading Jurisdictions ..." />
          )
          : (
            <div className="ecwizardContainer">
              <div className="ecwizardHeader">
                <div className="ecwizardHeaderLeft">
                  Jurisdictions
                </div>
              </div>
              <div className="ecwizardHeaderDescription">
                Choose the Jurisdictions that will be displayed in the wizard.
              </div>
              <hr />
              {
                jurisdictions && (
                  <>
                    <div className="ecwizardContentControls">
                      <div className="ecwizardContentButtons">
                        <div className="ecwizardContentButtonsLeft">
                          <div className="buttonItemBase">
                            <VtxButton
                              id="selectAllButton"
                              icon={<VtxIcon type="check-square" />}
                              onClick={onSelectAll}
                              type="default"
                            >
                              Select All
                            </VtxButton>
                          </div>
                          <div className="buttonItemBase">
                            <VtxButton
                              id="clearAllButton"
                              icon={<VtxIcon type="border" />}
                              onClick={onClearAll}
                              type="default"
                            >
                              Clear All
                            </VtxButton>
                          </div>
                        </div>
                        <div className="ecwizardContentButtonsRight">
                          <div className="buttonItemBase">
                            {selectionInformation}
                          </div>
                        </div>
                      </div>
                      <div className="ecwizardContentControlsBorder">
                        <VtxTree
                          checkable
                          checkStrictly
                          treeData={treeData}
                          checkedKeys={selectedJurisdictionKeys}
                          onCheck={onCheck}
                          defaultExpandedKeys={[]}
                        />
                      </div>
                      {renderValidationMessage('jurisdiction-tree', validationMessage)}
                    </div>
                    <Footer onCancel={onCancel} saveEnabled={canSave} onSave={onSave} />
                  </>
                )
              }
            </div>
          )
      }
      <Notification
        data={notification}
        onNotificationClose={onCloseNotification}
        autoClose
      />
    </>
  );
};

export default Jurisdictions;
